// 批次 19（B8-1，Gitea issue #14）：dashboard 頁型的變體設定表。
//
// KNOWN-NOT-DONE B8-1 註記「dashboard 頁型與內容設定表是 E: 環境那條任務線的設計，本機無
// dashboard 頁型與素材」——本檔即為本機重建的內容設定表：8 個 dashboard 變體的麵包屑／
// 文件標題／資料家族／視角／panel 欄位組等差異，全部集中在此，body 只吃 config 不寫死。
//
// 【證據等級】§7 第 3 級（原始碼）；每個變體的 JSP 標題／麵包屑／panel 標題行號於下方逐條。

import type { DashboardFamily } from '@/mocks/dashboard-store';

/** 訓練資訊 panel 的欄位組（各變體 JSP `.titleRow` 的標題順序）。 */
export type TrainingColumn =
  | '訓練階段'
  | '計畫表單'
  | '課程表單'
  | '階段成績'
  | '等第制'
  | 'EPA'
  | 'EPAs';

export interface DashboardVariantConfig {
  readonly pageId: string;
  /** JSP `<title>`（例如 studentMain.jsp:6）。 */
  readonly docTitle: string;
  /** 麵包屑第一層（breadcrumb.jsp param `breadcrumb1`）。 */
  readonly breadcrumb: string;
  /** 資料家族（mainUtils.js 取資料端點三分支）。 */
  readonly family: DashboardFamily;
  /** 視角：student＝menuRow 待辦盒＋訓練資訊 panel；teacher＝學員盒＋studentsRow；
   *  dept-admin（批次 20）＝待辦盒＋訓練計畫盒＋planTargetList 計畫面板（DeptAdminBody）。 */
  readonly perspective: 'student' | 'teacher' | 'dept-admin';
  /** menuRow 是否有行事曆盒（menu_Box_calendar_Tmpl；studentMain.js renderCalendarMenu）。 */
  readonly calendarMenuBox: boolean;
  /** 訓練資訊 panel 欄位組（student 變體；teacher 變體無此 panel＝空陣列）。 */
  readonly trainingColumns: readonly TrainingColumn[];
}

export const DASHBOARD_VARIANTS: Readonly<Record<string, DashboardVariantConfig>> = {
  // studentMain.jsp:6 `<title>學員首頁`／:49 breadcrumb1＝學員首頁／:60 公告欄／:84 訓練資訊
  // （titleRow：訓練階段/計畫表單/課程表單/等第制/EPA，studentMain.jsp:90-95）。
  'main-student-main': {
    pageId: 'main-student-main',
    docTitle: '學員首頁',
    breadcrumb: '學員首頁',
    family: 'ugy',
    perspective: 'student',
    calendarMenuBox: true,
    trainingColumns: ['訓練階段', '計畫表單', '課程表單', '等第制', 'EPA'],
  },
  // teacherMain.jsp:5 `<title>教師首頁`／:50 breadcrumb1＝教師首頁／:61 公告欄（無訓練資訊 panel；
  // studentsRow 學員資訊面板由 JS 動態加入，teacherMain.jsp:66）。
  'main-teacher-main': {
    pageId: 'main-teacher-main',
    docTitle: '教師首頁',
    breadcrumb: '教師首頁',
    family: 'ugy',
    perspective: 'teacher',
    calendarMenuBox: true,
    trainingColumns: [],
  },
  // dentisit/studentMain.jsp:6 `<title>首頁`（跟其他變體不同！）：49 breadcrumb1＝資訊面版／
  // :60 公告欄／:84 訓練資訊（titleRow 3 欄，:88-91；dentisit 無等第制/EPA 欄）。
  'main-dentisit-student-main': {
    pageId: 'main-dentisit-student-main',
    docTitle: '首頁',
    breadcrumb: '資訊面版',
    family: 'dentisit',
    perspective: 'student',
    calendarMenuBox: true,
    trainingColumns: ['訓練階段', '計畫表單', '課程表單'],
  },
  // dentisit/teacherMain.jsp:5 `<title>教師首頁`／:50 breadcrumb1＝資訊面版／:61 公告欄。
  'main-dentisit-teacher-main': {
    pageId: 'main-dentisit-teacher-main',
    docTitle: '教師首頁',
    breadcrumb: '資訊面版',
    family: 'dentisit',
    perspective: 'teacher',
    calendarMenuBox: true,
    trainingColumns: [],
  },
  // medicalAffairs/studentMain.jsp:6 `<title>學員首頁`／:50 breadcrumb1＝學員首頁／:61 公告欄／
  // :85 訓練資訊（titleRow 4 欄：訓練階段/計畫表單/階段成績/EPAs，:90-94；MA 用階段成績取代
  // 課程表單、表頭字是「EPAs」帶 s）。
  'main-medical-affairs-student-main': {
    pageId: 'main-medical-affairs-student-main',
    docTitle: '學員首頁',
    breadcrumb: '學員首頁',
    family: 'ma',
    perspective: 'student',
    calendarMenuBox: true,
    trainingColumns: ['訓練階段', '計畫表單', '階段成績', 'EPAs'],
  },
  // medicalAffairs/teacherMain.jsp:6 `<title>教師首頁`／:51 breadcrumb1＝教師首頁／:62 公告欄。
  'main-medical-affairs-teacher-main': {
    pageId: 'main-medical-affairs-teacher-main',
    docTitle: '教師首頁',
    breadcrumb: '教師首頁',
    family: 'ma',
    perspective: 'teacher',
    calendarMenuBox: true,
    trainingColumns: [],
  },
  // newMainExamList.jsp:6 `<title>首頁`／:62 breadcrumb1＝${informationPanelVar}（server 變數，
  // 即「資訊面版」）；內容＝commonMyExamList.jsp（S5 詳細）。此二頁為主頁的資訊面版子頁。
  'main-new-main-exam-list': {
    pageId: 'main-new-main-exam-list',
    docTitle: '首頁',
    breadcrumb: '資訊面版',
    family: 'ugy',
    perspective: 'student',
    calendarMenuBox: false,
    trainingColumns: [],
  },
  // newMainStudyRecord.jsp:6 `<title>首頁`／:61 breadcrumb1＝${informationPanelVar}。
  'main-new-main-study-record': {
    pageId: 'main-new-main-study-record',
    docTitle: '首頁',
    breadcrumb: '資訊面版',
    family: 'ugy',
    perspective: 'student',
    calendarMenuBox: false,
    trainingColumns: [],
  },
  // ── 批次 20 ──
  // deptAdminMain.jsp:5 `<title>教師首頁</title>`（頁面實名跟 menu 名「科部管理員首頁」不同，照實）；
  // :45 breadcrumb1＝科部管理員首頁；:51-82 menuRow／公告欄／planTargetList；資料端點群 main/ugy/deptAdmin/*（family ugy）。
  'main-dept-admin-main': {
    pageId: 'main-dept-admin-main',
    docTitle: '教師首頁',
    breadcrumb: '科部管理員首頁',
    family: 'ugy',
    perspective: 'dept-admin',
    calendarMenuBox: true,
    trainingColumns: [],
  },
  // medicalAffairs/clinicalMain.jsp:5 `<title>臨床教師首頁`（orphan 頁，R20-2 第 9 變體）；
  // :46 breadcrumb1＝臨床教師首頁；clinicalMain.js＝medicalAffairs/teacherMain.js 複本
  // （僅 TeacherMain→ClinicalMain 變數改名，diff :6/:7/:68/:279 四處）→ family ma＋perspective teacher。
  'main-medical-affairs-clinical-main': {
    pageId: 'main-medical-affairs-clinical-main',
    docTitle: '臨床教師首頁',
    breadcrumb: '臨床教師首頁',
    family: 'ma',
    perspective: 'teacher',
    calendarMenuBox: true,
    trainingColumns: [],
  },
};

export function dashboardVariant(pageId: string): DashboardVariantConfig | undefined {
  return DASHBOARD_VARIANTS[pageId];
}
